import React from "react";
import { ModalLevelInfo } from "@/pages/Games/modals/JoinGameModal/components/ModalLevelInfo/ModalLevelInfo";
import styles from "./CheckboxGroup.module.scss";
import CheckMarkIcon from "@/assets/icons/checkMark.svg";

interface CheckboxOption {
  label: string;
  value: string;
}

interface CheckboxGroupProps {
  selectedValues?: string[];
  onSelectionChange: (values: string[]) => void;
  options?: CheckboxOption[];
  setModalLevelVisible?: (level: string) => void;
  columns?: number;
  maxSelected?: number;
  hideInfo?: boolean;
}

const DEFAULT_OPTIONS: CheckboxOption[] = [
  { label: "Начинающий", value: "Начинающий" },
  { label: "Средний", value: "Средний" },
  { label: "Продвинутый", value: "Продвинутый" },
];

export const CheckboxGroup: React.FC<CheckboxGroupProps> = ({
  selectedValues = [],
  onSelectionChange,
  options = [],
  setModalLevelVisible,
  columns = 1,
  maxSelected = Infinity,
  hideInfo = false,
}) => {
  const [levelInfoModalVisible, setLevelInfoModalVisible] = React.useState(false);
  const [selectedLevelInfo, setSelectedLevelInfo] = React.useState<string>("");

  const checkboxOptions = options.length > 0 ? options : DEFAULT_OPTIONS;

  const handlePress = (value: string, disabled: boolean) => {
    if (disabled) return;

    let newSelectedValues: string[];
    if (selectedValues.includes(value)) {
      newSelectedValues = selectedValues.filter((item) => item !== value);
    } else {
      newSelectedValues = [...selectedValues, value];

      // Логика исключения: если выбран "Начинающий", убираем "Продвинутый" и наоборот
      if (value === "Начинающий" && newSelectedValues.includes("Продвинутый")) {
        newSelectedValues = newSelectedValues.filter((item) => item !== "Продвинутый");
      }

      if (value === "Продвинутый" && newSelectedValues.includes("Начинающий")) {
        newSelectedValues = newSelectedValues.filter((item) => item !== "Начинающий");
      }

      // Ограничение по maxSelected
      if (newSelectedValues.length > maxSelected) {
        const added = value;
        const kept = selectedValues.slice(-(maxSelected - 1));
        newSelectedValues = [...kept, added];
      }
    }
    onSelectionChange(newSelectedValues);
  };

  const handleLevelInfoClick = (level: string) => {
    if (setModalLevelVisible) {
      setModalLevelVisible(level);
    } else {
      setSelectedLevelInfo(level);
      setLevelInfoModalVisible(true);
    }
  };

  const containerClassName =
    columns === 2 ? styles.container_two_cols : styles.container;
  const itemClassName =
    columns === 2 ? styles.item_container_two_cols : styles.item_container;

  return (
    <>
      <div className={containerClassName}>
        {checkboxOptions.map((option) => {
          const isSelected = selectedValues.includes(option.value);
          const isDisabled = !isSelected && selectedValues.length >= maxSelected;

          return (
            <div
              key={option.value}
              className={`${itemClassName} ${isDisabled ? styles.item_disabled : ""}`}
            >
              <button
                type="button"
                className={styles.checkbox_container}
                onClick={() => handlePress(option.value, isDisabled)}
                disabled={isDisabled}
              >
                <div
                  className={`${styles.checkbox_square} ${
                    isSelected ? styles.checkbox_square_selected : styles.checkbox_square_unselected
                  }`}
                >
                  {isSelected && (
                    <img
                      src={CheckMarkIcon}
                      alt="checked"
                      className={styles.checkbox_icon}
                    />
                  )}
                </div>
                <span className={styles.checkbox_text}>{option.label}</span>
              </button>
              {!hideInfo && (
                <button
                  type="button"
                  className={styles.info_button}
                  onClick={() => handleLevelInfoClick(option.value)}
                  aria-label={`Информация об уровне ${option.label}`}
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M11 10.5H11.5C11.7761 10.5 12 10.7239 12 11V15C12 15.2761 12.2239 15.5 12.5 15.5H13M12 8.5H12.01"
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M13.3892 19.8785C14.4238 19.696 15.4124 19.3116 16.2984 18.7471C17.1844 18.1827 17.9506 17.4492 18.5532 16.5886C19.1558 15.728 19.583 14.7572 19.8104 13.7315C20.0378 12.7058 20.0609 11.6454 19.8785 10.6108C19.696 9.5762 19.3116 8.58765 18.7471 7.7016C18.1827 6.81556 17.4492 6.04937 16.5886 5.44678C15.728 4.8442 14.7572 4.41702 13.7315 4.18963C12.7058 3.96225 11.6454 3.93911 10.6108 4.12154C9.5762 4.30397 8.58765 4.6884 7.7016 5.25287C6.81556 5.81734 6.04937 6.55081 5.44678 7.41139C4.8442 8.27197 4.41702 9.24281 4.18963 10.2685C3.96225 11.2942 3.93911 12.3546 4.12154 13.3892C4.30397 14.4238 4.6884 15.4124 5.25287 16.2984C5.81734 17.1844 6.55081 17.9506 7.41139 18.5532C8.27197 19.1558 9.24281 19.583 10.2685 19.8104C11.2942 20.0378 12.3546 20.0609 13.3892 19.8785Z"
                      stroke="currentColor"
                    />
                  </svg>
                </button>
              )}
            </div>
          );
        })}
      </div>

      {!setModalLevelVisible && (
        <ModalLevelInfo
          isOpen={levelInfoModalVisible}
          setIsOpen={setLevelInfoModalVisible}
          level={selectedLevelInfo}
        />
      )}
    </>
  );
};
